import { useState, useEffect } from 'react';
import { fetchFAQs, fetchFAQCategories } from '../api/GlobalApi';

function FAQsPage() {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    try {
      setLoading(true);
      const [faqsData, categoriesData] = await Promise.all([
        fetchFAQs(),
        fetchFAQCategories()
      ]);
      setFaqs(Array.isArray(faqsData) ? faqsData : []);
      setCategories(['All', ...(Array.isArray(categoriesData) ? categoriesData : [])]);
    } catch (error) {
      console.error('Error loading FAQs:', error);
      setFaqs([]);
      setCategories(['All']);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    const matchesSearch = 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div style={styles.loaderContainer}>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  return (
    <main className="main-content">
      {/* Header Section */}
      <div style={styles.hero}>
        <article className="blog-article-header">
          <h1 className="hero-title">Frequently Asked Questions</h1>
          <p className="hero-subtitle" style={{ marginTop: '12px' }}>
            Find answers to common questions about our platform
          </p>
        </article>

        {/* Search Bar */}
        <div style={styles.searchSection}>
          <div style={styles.searchBox}>
            <svg style={styles.searchIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search for questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={styles.clearBtn}>
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="blog-filters">
        {categories.map((category) => (
          <button
            key={category}
            className={`blog-filter-btn ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {/* FAQs List */}
      <div style={styles.faqsContainer}>
        {filteredFAQs.length === 0 ? (
          <div style={styles.emptyState}>
            <svg style={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 style={styles.emptyTitle}>No questions found</h3>
            <p style={styles.emptyText}>
              Try adjusting your search or filter to find what you're looking for.
            </p>
          </div>
        ) : (
          <div style={styles.faqsList}>
            {filteredFAQs.map((faq) => (
              <div
                key={faq.id}
                style={{
                  ...styles.faqItem,
                  ...(expandedId === faq.id ? styles.faqItemExpanded : {})
                }}
              >
                <button onClick={() => toggleFAQ(faq.id)} style={styles.faqQuestion}>
                  <div style={styles.faqQuestionLeft}>
                    <span style={styles.categoryBadge}>{faq.category}</span>
                    <h3 style={styles.questionText}>{faq.question}</h3>
                  </div>
                  <svg
                    style={{
                      ...styles.chevron,
                      transform: expandedId === faq.id ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {expandedId === faq.id && (
                  <div style={styles.faqAnswer}>
                    <div 
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                      style={styles.answerText}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Section */}
      <div style={styles.contactSection}>
        <div style={styles.contactCard}>
          <svg style={styles.contactIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 style={styles.contactTitle}>Still have questions?</h3>
          <p style={styles.contactText}>
            Can't find the answer you're looking for? Please contact our support team.
          </p>
          <a href="/contact" style={styles.contactBtn}>
            Contact Support
          </a>
        </div>
      </div>
    </main>
  );
}

// Styles remain the same...
const styles = {
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  hero: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '80px 20px',
    textAlign: 'center',
    color: 'white',
    borderRadius: '20px',
    marginBottom: '40px',
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: '600',
    marginBottom: '16px',
    margin: 0,
  },
  heroSubtitle: {
    fontSize: '20px',
    opacity: 0.9,
    marginBottom: '40px',
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "6px solid #ddd",
    borderTop: "6px solid #3498db",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  searchSection: {
    maxWidth: "720px",
    margin: "0 auto 32px",
  },
  searchBox: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: "16px",
    width: "20px",
    height: "20px",
    color: "#5f6368",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    height: "56px",
    padding: "0 48px 0 48px",
    fontSize: "16px",
    border: "1px solid #dadce0",
    borderRadius: "28px",
    outline: "none",
    transition: "all 0.2s",
    backgroundColor: "#f8f9fa",
  },
  clearBtn: {
    position: "absolute",
    right: "16px",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "none",
    cursor: "pointer",
    color: "#5f6368",
    borderRadius: "50%",
    transition: "all 0.2s",
  },
  faqsContainer: {
    marginTop: "32px",
  },
  faqsList: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  faqItem: {
    background: "white",
    border: "1px solid #e8eaed",
    borderRadius: "12px",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
  faqItemExpanded: {
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    borderColor: "#1a73e8",
  },
  faqQuestion: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    background: "none",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    gap: "16px",
  },
  faqQuestionLeft: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  categoryBadge: {
    display: "inline-block",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: "500",
    color: "#1a73e8",
    backgroundColor: "#e8f0fe",
    borderRadius: "12px",
    width: "fit-content",
  },
  questionText: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#202124",
    margin: 0,
    lineHeight: "1.5",
  },
  chevron: {
    width: "24px",
    height: "24px",
    color: "#5f6368",
    transition: "transform 0.3s ease",
    flexShrink: 0,
  },
  faqAnswer: {
    padding: "0 24px 24px",
    animation: "fadeIn 0.3s ease",
  },
  answerText: {
    fontSize: "15px",
    color: "#5f6368",
    lineHeight: "1.6",
    margin: 0,
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    color: "#dadce0",
    margin: "0 auto 16px",
  },
  emptyTitle: {
    fontSize: "20px",
    fontWeight: "500",
    color: "#202124",
    marginBottom: "8px",
  },
  emptyText: {
    fontSize: "14px",
    color: "#5f6368",
  },
  contactSection: {
    marginTop: "60px",
    paddingTop: "60px",
    borderTop: "1px solid #e8eaed",
  },
  contactCard: {
    textAlign: "center",
    padding: "48px 32px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    color: "white",
  },
  contactIcon: {
    width: "48px",
    height: "48px",
    margin: "0 auto 20px",
  },
  contactTitle: {
    fontSize: "24px",
    fontWeight: "500",
    marginBottom: "12px",
  },
  contactText: {
    fontSize: "16px",
    opacity: "0.9",
    marginBottom: "24px",
    maxWidth: "500px",
    margin: "0 auto 24px",
  },
  contactBtn: {
    display: "inline-block",
    padding: "12px 32px",
    background: "rgba(255,255,255,0.2)",
    border: "1px solid rgba(255,255,255,0.3)",
    color: "white",
    borderRadius: "24px",
    textDecoration: "none",
    fontWeight: "500",
    transition: "all 0.2s",
    backdropFilter: "blur(10px)",
  },
};

const styleSheet = document.createElement("style");
styleSheet.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    .blog-article-title {
      font-size: 28px !important;
    }
  }

  @media (max-width: 480px) {
    .blog-article-title {
      font-size: 24px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default FAQsPage;